import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCell, useDeleteCell } from '../hooks/useCells';
import Card from '../components/Card';
import Button from '../components/Button';
import Skeleton from '../components/Skeleton';
import { useRole } from '../hooks/useRole';
import { ChevronLeft, Pencil, Trash2, Package, Calendar, Box, Truck, Zap, ChevronRight } from 'lucide-react';
export default function CellDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: cell, isLoading } = useCell(id || '');
  const deleteCell = useDeleteCell();
  const { isEditor } = useRole();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteCell.mutateAsync(id);
      navigate('/cells');
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to delete cell');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton variant="circle" className="w-8 h-8" />
          <Skeleton className="h-6 w-32" />
        </div>
        <Card className="!p-3 space-y-4">
          <Skeleton className="h-4 w-24" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </Card>
      </div>
    );
  }

  if (!cell) {
    return (
      <Card>
        <div className="text-center py-12">
          <Zap className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <p className="text-lg font-semibold text-gray-700 mb-2">Cell not found</p>
          <p className="text-sm text-gray-500 mb-6">The cell you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/cells')}>
            Back to Cells
          </Button>
        </div>
      </Card>
    );
  }

  const packUsage = cell.pack_usage || [];
  const canDelete = packUsage.length === 0 && !cell.pack_id;

  const InfoItem = ({ icon: Icon, label, value, iconBg = "bg-gray-100", iconColor = "text-gray-600" }: {
    icon: any;
    label: string;
    value: string | number;
    iconBg?: string;
    iconColor?: string;
  }) => (
    <div className="flex items-center gap-2.5 py-2">
      <div className={`w-7 h-7 rounded-md ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide leading-none mb-0.5">{label}</p>
        <p className="text-sm font-medium text-gray-900 truncate">{value}</p>
      </div>
    </div>
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Stock': return 'bg-green-100 text-green-700';
      case 'Used': return 'bg-blue-100 text-blue-700';
      case 'Reserved': return 'bg-yellow-100 text-yellow-700';
      case 'Scrapped': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/cells')}
          className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900 truncate">{cell.cell_id}</h1>
          {cell.batch_id && (
            <p className="text-xs text-gray-500">Batch: {cell.batch_id}</p>
          )}
        </div>
      </div>

      {isEditor && (
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/cells/${id}/edit`)}
            className="flex-1 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5 text-sm font-medium"
          >
            <Pencil className="w-4 h-4" />
            Edit
          </button>
          {canDelete && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-white border border-red-200 rounded-lg text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      <Card className="!p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-gray-600" />
            </div>
            <span className="text-sm font-medium text-gray-600">Status</span>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(cell.status || '')}`}>
            {cell.status || 'Unknown'}
          </span>
        </div>
      </Card>

      <Card className="!p-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cell Information</h3>
        <div className="grid grid-cols-2 gap-x-4">
          {cell.cell_capacity_kwh && (
            <InfoItem icon={Zap} label="Capacity" value={`${cell.cell_capacity_kwh} kWh`} iconBg="bg-blue-50" iconColor="text-blue-600" />
          )}
          {cell.received_box && (
            <InfoItem icon={Box} label="Received Box" value={cell.received_box} />
          )}
          {cell.shipment_id && (
            <InfoItem icon={Truck} label="Shipment ID" value={cell.shipment_id} />
          )}
          {cell.received_date && (
            <InfoItem icon={Calendar} label="Received" value={new Date(cell.received_date).toLocaleDateString()} />
          )}
        </div>
      </Card>

      {cell.pack_id && (
        <Card className="!p-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Current Pack</h3>
          <Link
            to={`/packs/${cell.pack_id}`}
            className="flex items-center gap-3 p-2.5 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Package className="w-4 h-4 text-blue-600" />
            </div>
            <span className="flex-1 text-sm font-semibold text-blue-700 truncate">{cell.pack_id}</span>
            <ChevronRight className="w-4 h-4 text-blue-400" />
          </Link>
        </Card>
      )}

      {packUsage.length > 0 && (
        <Card className="!p-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Pack Usage History
          </h3>
          <div className="space-y-2">
            {packUsage.map((usage, index) => (
              <Link
                key={index}
                to={`/packs/${usage.pack_id}`}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                  <Package className="w-3 h-3 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-gray-700 truncate block">{usage.pack_id}</span>
                  {usage.position && (
                    <span className="text-[10px] text-gray-500">Position: {usage.position}</span>
                  )}
                </div>
                <ChevronRight className="w-3 h-3 text-gray-400" />
              </Link>
            ))}
          </div>
        </Card>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-4">
            <h3 className="text-base font-bold text-gray-900 mb-2">Delete Cell?</h3>
            <p className="text-sm text-gray-500 mb-4">
              This will permanently delete <span className="font-medium text-gray-700">{cell.cell_id}</span>.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={deleteCell.isPending}
                className="flex-1 py-2 px-4 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteCell.isPending ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteCell.isPending}
                className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

